import React from "react";
import FormProvider from "@providers/FormProvider";
import Tabs from "@components/Tabs";
import Button from "@components/Button";
import ContentTab from "@components/Offers/ContentTab";
import styles from "./SectionTabs.module.scss";
import { Form, useFormik } from "formik";
import cx from "classnames";
import OfferService from "@services/OfferService";
import { usePageContext } from "@providers/PageProvider";
import useFilter from "@hooks/useFilter";
import usePagination from "@hooks/usePagination";
import helper from "@utils/helper";
import _ from "lodash";
import { useTranslationContext } from "@providers/TranslationProvider";
import { useLocomotiveContext } from "@providers/LocomotiveProvider";

const View = () => {
  const { offerCategories, hotel } = usePageContext();
  const { i18n } = useTranslationContext();
  const { scroll } = useLocomotiveContext();
  const categories: any = _.map(offerCategories, helper.formatCategory);
  // TODO Refactor: cần viết một bản useFilter chỉ support 1 filter đơn giản hơn, không support cho thư viện riêng lẻ nào. Source này đang sử dụng lại từ useFilterForm (support antd) là không hợp lý.
  const filter = useFilter({
    page: 1,
    pageSize: 6,
  });

  const form: any = useFormik({
    initialValues: {
      tab: "all_offers",
    },
    onSubmit: async () => {
      return true;
    },
  });

  const currentTab = form.values.tab;
  const filterGetUrlQuery = filter.getUrlQuery;
  const hotelRef = React.useRef(hotel);
  const i18nLanguageRef = React.useRef(i18n.language);
  const tabRef = React.useRef(currentTab);
  const filterRef = React.useRef(filter);
  
  React.useEffect(() => {
    hotelRef.current = hotel;
    i18nLanguageRef.current = i18n.language;
    tabRef.current = currentTab;
    filterRef.current = filter;
  }, [hotel, i18n.language, currentTab, filter]);

  const paginationModel = usePagination({
    initialParams: {
      type: "infinity",
      ...filter.getPaginationQuery(),
    },
    loader: async (config: any) => {
      const res = await OfferService.search(
        filterGetUrlQuery({
          populate: ["thumbnail", "category"],
          locale: i18nLanguageRef.current,
          page: _.get(config, "page"),
          filters: {
            ...(tabRef.current === "all_offers"
              ? {}
              : { category: { slug: { $eq: tabRef.current } } }),
            hotel: {
              slug: {
                $eq: hotelRef.current,
              },
            },
          },
        })
      );
      return {
        data: _.get(res, "data", []),
        pageSize: _.get(res, "meta.pagination.pageSize", 0),
        total: _.get(res, "meta.pagination.total", 0),
      };
    },
  });

  const offers = React.useMemo(() => _.map(paginationModel.items, helper.formatOfferItem), [paginationModel.items]);

  const TABS = React.useMemo(() => {
    const tabs = [
      {
        label: "all offers",
        key: "all_offers",
        value: "all_offers",
        children: <ContentTab data={offers} />,
      },
    ];
    _.map(categories, (cat) => {
      tabs.push({
        label: cat.name,
        key: cat.slug,
        value: cat.slug,
        children: <ContentTab data={offers} />,
      });
    });
    return tabs;
  }, [offers, categories]);

  // Trigger call data when did mount.
  React.useEffect(() => {
    const pageQuery = filterRef.current.getPageQuery();
    paginationModel.onLoadPage(pageQuery);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Trigger call data when tab changes.
  React.useEffect(() => {
    if (currentTab) {
      paginationModel.onReload();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTab]);

  // Update locomotive scroll when offers change to ensure images are visible
  React.useEffect(() => {
    if (scroll && offers.length > 0) {
      // Use a small delay to ensure DOM is updated
      const timer = setTimeout(() => {
        scroll.update();
        // Manually add is-inview class to visible images that have data-scroll
        const scrollElements = document.querySelectorAll('[data-scroll]');
        scrollElements.forEach((el) => {
          const rect = el.getBoundingClientRect();
          const isVisible = rect.top < window.innerHeight && rect.bottom > 0;
          if (isVisible) {
            el.classList.add('is-inview');
          }
        });
        // Force another update after a short delay
        setTimeout(() => {
          scroll.update();
        }, 150);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [offers, scroll]);
  const onLoadMore = () => {
    paginationModel.onNextPage();
  };
  return (
    <section data-scroll-section className={cx("inner", styles.section)}>
      <FormProvider form={form}>
        <Form>
          <Tabs
            name="tab"
            className={styles.sectionTab}
            defaultActiveKey={"all_offers"}
            items={TABS}
          />
          <div className={`pt-[6.5625rem]`}>
            {!!paginationModel.hasNextPage && (
              <Button
                className="block mx-auto"
                label="load more"
                onClick={onLoadMore}
              />
            )}
          </div>
        </Form>
      </FormProvider>
    </section>
  );
};
export default View;
